import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";

const PageHeader = styled.section`
  height: 400px;
  background: linear-gradient(rgba(220, 38, 38, 0.8), rgba(185, 28, 28, 0.8)), url("/images/srv_bg.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  .header-content {
    text-align: center;

    h1 {
      color: white;
      font-size: 3.5rem;
      margin-bottom: 1rem;
    }

    p {
      font-size: 1.2rem;
      max-width: 600px;
      margin: 0 auto;
    }
  }

  @media (max-width: 768px) {
    height: 300px;

    .header-content h1 {
      font-size: 2.5rem;
    }
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: white;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  .service-card {
    background: white;
    border-radius: 15px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-10px);
    }

    .service-icon {
      width: 80px;
      height: 80px;
      background: #dc2626;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;
      font-size: 2rem;
    }

    h3 {
      color: #dc2626;
      margin-bottom: 1rem;
      font-size: 1.3rem;
    }

    p {
      color: #666;
      line-height: 1.6;
      margin-bottom: 1rem;
    }

    ul {
      list-style: none;
      padding: 0;

      li {
        padding: 0.5rem 0;
        color: #666;
        position: relative;
        padding-left: 1.5rem;

        &:before {
          content: "✓";
          position: absolute;
          left: 0;
          color: #dc2626;
          font-weight: bold;
        }
      }
    }
  }
`;

const FormSection = styled.section`
  margin: 4rem 0;
  padding: 3rem;
  background: #f8f9fa;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h2`
  text-align: center;
  color: #dc2626;
  margin-bottom: 2rem;
  font-size: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select,
  textarea {
    padding: 12px 15px;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    font-size: 16px;
    transition: border-color 0.3s ease;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: #dc2626;
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }

    &.error {
      border-color: #e74c3c;
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  .error-message {
    color: #e74c3c;
    font-size: 14px;
    margin-top: 0.5rem;
  }
`;

const SubmitButton = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover:not(:disabled) {
    background: #b91c1c;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(220, 38, 38, 0.3);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const InfoSection = styled.div`
  .info-card {
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;

    h3 {
      color: #dc2626;
      margin-bottom: 1rem;
    }

    p {
      color: #666;
      line-height: 1.6;
      margin-bottom: 1rem;
    }
  }

  .contact-info {
    background: #dc2626;
    color: white;
    padding: 2rem;
    border-radius: 10px;
    text-align: center;

    h3 {
      color: white;
      margin-bottom: 1rem;
    }

    .contact-item {
      margin: 1rem 0;

      a {
        color: white;
        font-weight: 500;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const TrailerRepair = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    trailerType: "",
    serviceType: "",
    urgency: "",
    location: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    {
      icon: "🔧",
      title: "Mechanical Repairs",
      description: "Complete mechanical repair services for all trailer types.",
      items: [
        "Brake system repairs and maintenance",
        "Suspension system servicing",
        "Axle repairs and replacement",
        "Bearing and hub services",
        "Coupling and hitch repairs",
      ],
    },
    {
      icon: "⚡",
      title: "Electrical Services",
      description: "Professional electrical repairs and installations.",
      items: [
        "LED lighting upgrades",
        "Wiring harness repairs",
        "Connector replacements",
        "Brake controller installations",
        "Safety system upgrades",
      ],
    },
    {
      icon: "🛠️",
      title: "Structural Repairs",
      description: "Expert structural repairs and modifications.",
      items: ["Welding and fabrication", "Frame straightening", "Floor and side repairs", "Custom modifications", "Rust treatment and prevention"],
    },
    {
      icon: "🔍",
      title: "Inspections & Maintenance",
      description: "Comprehensive inspection and preventive maintenance.",
      items: ["Safety inspections", "Roadworthy certificates", "Preventive maintenance", "Component testing", "Compliance certifications"],
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.trailerType.trim()) {
      newErrors.trailerType = "Trailer type is required";
    }

    if (!formData.serviceType.trim()) {
      newErrors.serviceType = "Service type is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        to_email: "contact@ausgriptyres.com.au",
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        company: formData.company,
        trailer_type: formData.trailerType,
        service_type: formData.serviceType,
        urgency: formData.urgency,
        location: formData.location,
        description: formData.description,
        subject: `Trailer tyre repair request from ${formData.name}`,
      };

      const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID || process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (SERVICE_ID && TEMPLATE_ID && USER_ID) {
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, payload, USER_ID);
      } else {
        console.log("EmailJS not configured — payload:", payload);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        trailerType: "",
        serviceType: "",
        urgency: "",
        location: "",
        description: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to send request. Please try again or contact us directly.");
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <PageHeader>
        <div className="container">
          <motion.div
            className="header-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            <h1>Trailer tyre repair services</h1>
            <p>
              Professional trailer tyre repair and maintenance services for all types of trailers. From minor repairs to major overhauls, we've got
              you covered.
            </p>
          </motion.div>
        </div>
      </PageHeader>

      <ContentSection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}>
            <div className="section-title">
              <h2>Our trailer tyre repair services</h2>
              <p>
                We provide comprehensive trailer tyre repair services with experienced technicians, quality parts, and competitive pricing. Our mobile
                service can come to your location for convenient on-site repairs.
              </p>
            </div>

            <ServicesGrid>
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  className="service-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}>
                  <div className="service-icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul>
                    {service.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </ServicesGrid>

            {isSubmitted ? (
              <FormSection>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}>
                  <SuccessMessage>
                    <h3>Thank you for your service request!</h3>
                    <p>
                      We have received your trailer tyre repair request and will contact you within 2 hours to discuss your requirements and schedule
                      a service appointment.
                    </p>
                    <button
                      className="btn"
                      onClick={() => setIsSubmitted(false)}
                      style={{ marginTop: "1rem" }}>
                      Submit Another Request
                    </button>
                  </SuccessMessage>
                </motion.div>
              </FormSection>
            ) : (
              <FormSection>
                <FormTitle>Request trailer tyre repair service</FormTitle>

                <FormGrid>
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}>
                    <FormContainer onSubmit={handleSubmit}>
                      <FormGroup>
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={errors.name ? "error" : ""}
                          placeholder="Enter your full name"
                        />
                        {errors.name && <span className="error-message">{errors.name}</span>}
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={errors.email ? "error" : ""}
                          placeholder="Enter your email address"
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={errors.phone ? "error" : ""}
                          placeholder="Enter your phone number"
                        />
                        {errors.phone && <span className="error-message">{errors.phone}</span>}
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="company">Company Name</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Enter your company name (optional)"
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="trailerType">Trailer Type *</label>
                        <select
                          id="trailerType"
                          name="trailerType"
                          value={formData.trailerType}
                          onChange={handleChange}
                          className={errors.trailerType ? "error" : ""}>
                          <option value="">Select trailer type</option>
                          <option value="Box Trailer">Box Trailer</option>
                          <option value="Flatbed Trailer">Flatbed Trailer</option>
                          <option value="Semi-Trailer">Semi-Trailer</option>
                          <option value="Car Carrier">Car Carrier</option>
                          <option value="Refrigerated Trailer">Refrigerated Trailer</option>
                          <option value="Tanker Trailer">Tanker Trailer</option>
                          <option value="Low Loader">Low Loader</option>
                          <option value="Other">Other</option>
                        </select>
                        {errors.trailerType && <span className="error-message">{errors.trailerType}</span>}
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="serviceType">Service Required *</label>
                        <select
                          id="serviceType"
                          name="serviceType"
                          value={formData.serviceType}
                          onChange={handleChange}
                          className={errors.serviceType ? "error" : ""}>
                          <option value="">Select service type</option>
                          <option value="Brake Repair">Brake Repair</option>
                          <option value="Electrical Repair">Electrical Repair</option>
                          <option value="Structural Repair">Structural Repair</option>
                          <option value="Suspension Repair">Suspension Repair</option>
                          <option value="Inspection">Inspection</option>
                          <option value="Maintenance">Preventive Maintenance</option>
                          <option value="Emergency Repair">Emergency Repair</option>
                          <option value="Other">Other</option>
                        </select>
                        {errors.serviceType && <span className="error-message">{errors.serviceType}</span>}
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="urgency">Urgency Level</label>
                        <select
                          id="urgency"
                          name="urgency"
                          value={formData.urgency}
                          onChange={handleChange}>
                          <option value="">Select urgency</option>
                          <option value="Emergency">Emergency (Same Day)</option>
                          <option value="Urgent">Urgent (Within 24 hours)</option>
                          <option value="Standard">Standard (Within 3 days)</option>
                          <option value="Flexible">Flexible (Within a week)</option>
                        </select>
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="location">Service Location</label>
                        <input
                          type="text"
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          placeholder="Address or location for service"
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="description">Problem Description *</label>
                        <textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          className={errors.description ? "error" : ""}
                          placeholder="Please describe the problem or service required in detail..."
                        />
                        {errors.description && <span className="error-message">{errors.description}</span>}
                      </FormGroup>

                      <SubmitButton
                        type="submit"
                        disabled={isSubmitting}>
                        {isSubmitting ? "Sending Request..." : "Request Service"}
                      </SubmitButton>
                    </FormContainer>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}>
                    <InfoSection>
                      <div className="info-card">
                        <h3>Why choose our trailer tyre repair service?</h3>
                        <p>With years of experience in trailer tyre repairs and maintenance, we provide:</p>
                        <ul style={{ listStyle: "none", padding: 0 }}>
                          <li style={{ padding: "0.5rem 0", paddingLeft: "1.5rem", position: "relative" }}>
                            <span style={{ position: "absolute", left: 0, color: "#dc2626", fontWeight: "bold" }}>✓</span>
                            24/7 emergency repair services
                          </li>
                          <li style={{ padding: "0.5rem 0", paddingLeft: "1.5rem", position: "relative" }}>
                            <span style={{ position: "absolute", left: 0, color: "#dc2626", fontWeight: "bold" }}>✓</span>
                            Mobile service - we come to you
                          </li>
                          <li style={{ padding: "0.5rem 0", paddingLeft: "1.5rem", position: "relative" }}>
                            <span style={{ position: "absolute", left: 0, color: "#dc2626", fontWeight: "bold" }}>✓</span>
                            Certified technicians and quality parts
                          </li>
                          <li style={{ padding: "0.5rem 0", paddingLeft: "1.5rem", position: "relative" }}>
                            <span style={{ position: "absolute", left: 0, color: "#dc2626", fontWeight: "bold" }}>✓</span>
                            Competitive pricing with upfront quotes
                          </li>
                          <li style={{ padding: "0.5rem 0", paddingLeft: "1.5rem", position: "relative" }}>
                            <span style={{ position: "absolute", left: 0, color: "#dc2626", fontWeight: "bold" }}>✓</span>
                            Warranty on all repairs
                          </li>
                        </ul>
                      </div>

                      <div className="contact-info">
                        <h3>Emergency Service Available</h3>
                        <div className="contact-item">
                          <strong>Call us now:</strong>
                          <br />
                          <a href="tel:0413960242">0413960242</a>
                        </div>
                        <div className="contact-item">
                          <strong>Email:</strong>
                          <br />
                          <a href="mailto:contact@ausgriptyres.com.au">contact@ausgriptyres.com.au</a>
                        </div>
                        <p>For emergency repairs, call us directly for immediate assistance.</p>
                      </div>
                    </InfoSection>
                  </motion.div>
                </FormGrid>
              </FormSection>
            )}
          </motion.div>
        </div>
      </ContentSection>
    </>
  );
};

export default TrailerRepair;
